<script setup lang="ts">
import type SchemaChild from "~/types/SchemaChild";
import type Schema from "~/types/Schema";
import { DATATYPES } from "~/constants/dataTypes";
import useLowerCamelCase from "../../utils/useLowerCamelCase";

const props = defineProps({
  schema: {
    type: Object as PropType<Schema>,
    required: true,
  },
  children: {
    type: Array as PropType<SchemaChild[]>,
    default: () => [],
  },
});

const search = ref("");
// Filter out children based on the search term.
const filteredChildren = computed(() =>
  props.children.filter(
    (child) =>
      child.recordId.toLowerCase().includes(search.value.toLowerCase()) ||
      child.childCamelCaseName.toLowerCase().includes(search.value.toLowerCase()) ||
      child.childDescription.toLowerCase().includes(search.value.toLowerCase()),
  ),
);

function linkDataType(dataType: string) {
  if (DATATYPES[dataType.toLowerCase()] && dataType.toLowerCase() !== "array") {
    return `/${dataType.toLowerCase()}`;
  }
  return "";
}
</script>

<template>
  <div class="p-4">
    <div class="mb-4 text-right">
      <SearchBox v-model="search" class="lg:inline-block lg:w-1/3" />
    </div>

    <!-- List items -->
    <ListItems :items="filteredChildren" :apply-row-link="false">
      <template #header>
        <div class="mr-2 flex-none lg:w-1/4">Name</div>

        <div class="mr-2 flex-none lg:w-1/6">DataType</div>

        <div class="flex-1">Description</div>
      </template>

      <template #row="{ item, useXL }">
        <div
          class="mr-2 flex-none overflow-hidden font-bold lg:w-1/4"
          :class="useXL ? 'xl:font-normal' : 'lg:font-normal'"
        >
          <NuxtLink v-if="schemaLink(item as SchemaChild)" :to="schemaLink(item as SchemaChild)">
            {{ useLowerCamelCase(item) ? item.childLowerCamelName : item.childCamelCaseName }}
          </NuxtLink>

          <span v-else class="text-grc-gray-darker">{{
            useLowerCamelCase(item) ? item.childLowerCamelName : item.childCamelCaseName
          }}</span>
        </div>

        <div
          class="mr-2 flex-none overflow-hidden font-bold capitalize lg:w-1/6"
          :class="useXL ? 'xl:font-normal' : 'lg:font-normal'"
        >
          <NuxtLink v-if="linkDataType(item.childDataType)" :to="linkDataType(item.childDataType)">
            {{ item.childDataType }}
          </NuxtLink>

          <span v-else class="text-grc-gray-darker">{{ item.childDataType }}</span>
        </div>

        <div class="mt-2 flex-1 lg:mt-0">{{ item.childDescription }}</div>
      </template>
    </ListItems>
  </div>
</template>
<style scoped>
a {
  @apply hover:text-schema-darker hover:underline;
}
</style>
